<template>
  <CallWrapper v-if="!isPublicRoute && isUserAuthenticated" />
  <VoiceAgentWrapper v-if="!isPublicRoute && isUserAuthenticated" />
  <SupportWrapper v-if="!isPublicRoute && isUserAuthenticated" />
  <NavigateToMobileModal v-if="!isPublicRoute && isUserAuthenticated" />
  <DashboardWrapper v-if="!isPublicRoute && isUserAuthenticated" />
  <SpaceWrapper v-if="!isPublicRoute && isUserAuthenticated" />
  <ChatWrapper v-if="!isPublicRoute && isUserAuthenticated" />
</template>

<script setup>
import { onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { themeTypes } from "@/src/constants/themeConstants.js";
import { useAuthStore } from "@/src/composables/useAuthStore.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import DashboardService from "@/src/api/DashboardService.js";
import NavigateToMobileModal from "@/src/components/NavigateToMobileModal.vue";
import VoiceAgentWrapper from "@/src/components/voice_agents/VoiceAgentWrapper.vue";
import SpaceWrapper from "@/src/components/spaces_components/spaces/SpaceWrapper.vue";
import CallWrapper from "@/src/components/telephony_components/modules/CallWrapper.vue";
import SupportWrapper from "@/src/components/inbound_agent_components/SupportWrapper.vue";
import ChatWrapper from "@/src/components/projects_components/chats/wrappers/ChatWrapper.vue";
import DashboardWrapper from "@/src/components/dashboard_components/wrappers/DashboardWrapper.vue";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const isUserAuthenticated = computed(() => authStore.isAuthenticated.value);
const isPublicRoute = computed(() => route.meta.public === true);

const initializeTheme = async () => {
  if (document.documentElement.getAttribute("data-theme")) {
    return;
  }

  if (isPublicRoute.value) {
    await store.dispatch("theme/initTheme", themeTypes.LIGHT);
    return;
  }

  try {
    const response = await DashboardService.fetchWorkspaces();

    if (response?.success && response?.profile?.theme) {
      await store.dispatch("theme/initTheme", response.profile.theme);
    } else {
      await store.dispatch("theme/initTheme", themeTypes.LIGHT);
    }
  } catch (error) {
    console.error("Error initializing theme:", error);
    await store.dispatch("theme/initTheme", themeTypes.LIGHT);
  }
};

const handleAppInstalled = () => {
  // Backend seeds policies asynchronously after install — wait a beat so the
  // fetched list reflects the newly created ones.
  setTimeout(() => {
    store.dispatch("rbac/fetchABACPolicies", { force: true });
  }, 2000);
};

onMounted(async () => {
  await router.isReady();
  await initializeTheme();

  if (!isPublicRoute.value && isUserAuthenticated.value) {
    store.dispatch("users/fetchUsersAndTeams");
    store.dispatch("objects/fetchObjects");
    store.dispatch("calls/fetchDispositions");
    store.dispatch("dialerSettings/fetchDialerSettings");
    store.dispatch("workspace/fetchUserTimezone");
    eventBus.on("app-installed", handleAppInstalled);
  }
});

onBeforeUnmount(() => {
  eventBus.off("app-installed", handleAppInstalled);
});
</script>
